import { useNavigate } from 'react-router-dom';

import { FilterPills } from '../../../../../../../components/filter-pills';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import NaturalLanguageSearchBox from '@/components/molecules/NaturalLanguageSearchBox';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { FilterPill } from '@/utils/buildFilterPills';

interface DocumentEditorToolbarProps {
  caseId: string;
  openDownloadModal: () => void;
  openDownloadZipModal: () => void;
  openDownloadAllZipModal: () => void;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  onSearchCall: (value: string, useNaturalLanguage: boolean) => void;
  clearSearch: () => void;
  processingNL?: boolean;
  currentPage: number;
  totalPages?: number;
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (page: number) => void;
  setFiltersModalOpen: (open: boolean) => void;
  filterCount: number;
  setSortModalOpen: (open: boolean) => void;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  hasActiveFilters: boolean;
  totalCount?: number;
  totalUnfilteredCount?: number;
  totalProcessedCount?: number;
  filterPills: FilterPill[];
  onClearAllFilters: () => void;
}

export function DocTableHeader({
  caseId,
  openDownloadModal,
  openDownloadZipModal,
  openDownloadAllZipModal,
  globalFilter,
  setGlobalFilter,
  onSearchCall,
  clearSearch,
  currentPage,
  totalPages,
  prevPage,
  nextPage,
  goToPage,
  setFiltersModalOpen,
  filterCount,
  setSortModalOpen,
  pageSize,
  onPageSizeChange,
  hasActiveFilters,
  processingNL,
  totalCount,
  totalUnfilteredCount,
  filterPills,
  onClearAllFilters,
}: DocumentEditorToolbarProps) {
  const navigate = useNavigate();
  return (
    <div className="space-y-3 border-b p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={() => {
              trackCustomEvent(TrackingEvents.ClickAddDocuments);
              navigate(`/app/chronos/case-editor/settings/documents?caseId=${caseId}`);
            }}
          >
            <span className="iconify lucide--plus" />
            Add Documents to Subset
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <span className="iconify lucide--download" />
                <span className="font-medium">Download</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {hasActiveFilters ? (
                <>
                  <DropdownMenuItem onClick={openDownloadZipModal}>Download Filtered Zip</DropdownMenuItem>
                  <DropdownMenuItem onClick={openDownloadAllZipModal}>Download All Zip</DropdownMenuItem>
                  <DropdownMenuItem onClick={openDownloadModal}>Download List</DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onClick={openDownloadModal}>Download List</DropdownMenuItem>
                  <DropdownMenuItem onClick={openDownloadAllZipModal}>Download All Zip</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={() => setSortModalOpen(true)}>
            <span className="iconify lucide--sort-asc" />
            Sort
          </Button>
          <Button onClick={() => setFiltersModalOpen(true)} className="relative">
            <span className="iconify lucide--filter" />
            Filter
            {filterCount > 0 && (
              <span className="absolute -top-2 -right-2 flex size-4.5 items-center justify-center rounded-full border border-teal-400 bg-teal-50 pt-[1px] text-xs font-medium text-teal-600">
                {filterCount}
              </span>
            )}
          </Button>

          <Pagination
            canGetPrevPage={currentPage > 0}
            canGetNextPage={totalPages !== undefined && currentPage < totalPages - 1}
            prevPage={prevPage}
            nextPage={nextPage}
            currentPage={currentPage}
            noOfPages={totalPages}
            goToPage={goToPage}
            onPageSizeChange={onPageSizeChange}
            pageSize={pageSize}
            totalCount={totalUnfilteredCount}
            filteredCount={totalCount}
            entityName="documents"
          />
        </div>
      </div>

      {/* Search box */}
      <div className="w-full">
        <NaturalLanguageSearchBox
          value={globalFilter}
          onChange={setGlobalFilter}
          onSearchCall={onSearchCall}
          clearSearch={clearSearch}
          processingNL={processingNL}
          entityType="documents"
        />
      </div>

      <FilterPills pills={filterPills} onClearAll={onClearAllFilters} />
    </div>
  );
}
